import axios, { type AxiosError, type AxiosInstance } from 'axios';
import { getApiError } from '@centric-os/helpers';
import { useAuthStore, useLoadingStore } from '@centric-os/stores';
import type { ApiResponseError, AxiosCustomConfig } from '@centric-os/types';
import { isEmpty, isNil } from 'lodash-es';

let authStore: any = null;
let loadingStore: any = null;

const baseURL: string = import.meta.env.VITE_CDL_ENDPOINT_BASE_URL;

export const createAxiosInstance = (config: AxiosCustomConfig = {}): AxiosInstance => {
  const api = axios.create({
    baseURL,
    ...config,
    paramsSerializer: (params: Record<string, any>): string => {
      const searchParams = new URLSearchParams();

      Object.keys(params).forEach((key) => {
        const value = params[key];

        if (
          isNil(value) || // Remove null or undefined
          ((Array.isArray(value) || typeof value === 'object') && isEmpty(value)) // Remove empty arrays and objects
        ) {
          return;
        }

        if (Array.isArray(value)) {
          // Serialize arrays as repeated keys to follow standard CDL params format
          value.forEach((item) => searchParams.append(key, item.toString()));
        } else if (typeof value === 'object') {
          // Serialize objects as JSON
          searchParams.append(key, JSON.stringify(value));
        } else {
          // Serialize primitives as strings
          searchParams.append(key, value.toString());
        }
      });

      return searchParams.toString();
    },
  });

  api.interceptors.request.use(async (req: AxiosCustomConfig) => {
    if (!loadingStore) {
      loadingStore = useLoadingStore();
    }

    if (req.url?.startsWith('/user/auth')) return req;

    if (!authStore) {
      authStore = useAuthStore();
    }

    const token = await authStore.getValidAccessToken();

    if (token) {
      if (!config?.skipAuthorization && !req.headers.authorization) {
        req.headers.authorization = `Bearer ${token}`;

        loadingStore.loading = true;
      } else if (config?.skipAuthorization) {
        loadingStore.loading = true;
      }
    }

    return req;
  });

  api.interceptors.response.use(
    (response) => {
      loadingStore.loading = false;

      return response;
    },
    (error: AxiosError<ApiResponseError>) => {
      loadingStore.loading = false;
      return Promise.reject(getApiError(error));
    },
  );

  return api;
};
